import { Injectable, OnDestroy, signal } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map, Observable, pairwise, Subscription } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class LoaderService implements OnDestroy {

  progressValue = signal(0);
  showLoader = signal(false);
  private subscription: Subscription;
  
  private trickleInterval: ReturnType<typeof setInterval> | null = null;
  private completionTimeout: ReturnType<typeof setTimeout> | null = null;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;
  private trickleValue = 0;


    readonly activeRequests$ = new BehaviorSubject<number>(0);

    constructor(){
      this.subscription = this.activeRequests$.pipe(
        pairwise()
      ).subscribe(([prev, curr]) => {
        if (prev === 0 && curr > 0) {
          this.onLoadingStart();
        } else if (curr > 0 && curr < prev) {
          this.onRequestComplete();
        } else if (curr === 0 && prev > 0) {
          this.onRequestComplete();
          this.startCompletionDelay();
        } else if (curr > prev && this.showLoader()) {
          this.clearCompletionTimeout();
        }
      });
    }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.clearTrickle();
    this.clearCompletionTimeout();
    this.clearHideTimeout();
  }

    isLoading$: Observable<boolean> = this.activeRequests$.pipe(
      map(count => count > 0),
      distinctUntilChanged()
    );
  
    increment(): void {
      this.activeRequests$.next(this.activeRequests$.value + 1);
    }
  
    decrement(): void {
      if (this.activeRequests$.value > 0) {
        this.activeRequests$.next(this.activeRequests$.value - 1);
      }
    }

  private clearCompletionTimeout(): void {
    if (this.completionTimeout !== null) {
      clearTimeout(this.completionTimeout);
      this.completionTimeout = null;
    }
  }

  private onLoadingStart(): void {
    this.clearCompletionTimeout();
    this.clearHideTimeout();
    if (!this.showLoader()) {
      this.progressValue.set(0);
      this.trickleValue = 0;
      this.showLoader.set(true);
      this.startTrickle();
    }
  }

  
  private startTrickle(): void {
    this.clearTrickle();
    this.trickleInterval = setInterval(() => {
      if (this.trickleValue < 20) {
        const remaining = 20 - this.trickleValue;
        this.trickleValue += Math.max(0.5, remaining * 0.08);
        if (this.trickleValue > this.progressValue() && this.showLoader()) {
          this.progressValue.set(this.trickleValue);
        }
      } else {
        this.clearTrickle();
      }
    }, 200);
  }
  
  private clearHideTimeout(): void {
    if (this.hideTimeout !== null) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  private clearTrickle(): void {
    if (this.trickleInterval !== null) {
      clearInterval(this.trickleInterval);
      this.trickleInterval = null;
    }
  }

  
  private onRequestComplete(): void {
    this.clearCompletionTimeout();
    this.progressValue.update(v => v + (90 - v) * 0.3);
  }

  private startCompletionDelay(): void {
    this.clearCompletionTimeout();
    this.completionTimeout = setTimeout(() => {
      this.clearTrickle();
      this.progressValue.set(100);
      this.hideTimeout = setTimeout(() => {
        this.showLoader.set(false);
        this.progressValue.set(0);
        this.trickleValue = 0;
      }, 200);
    }, 150);
  }
}
